<!-- gap-check.html -->
@if (showAlert) {
  <app-alert-modal
    [key]="alertKey"
    [isOpen]="showAlert"
    [title]="alertTitle"
    [message]="alertMessage"
    buttonText="Close"
    [isLoading]="isLoading"
    [type]="alertType"
    (closed)="showAlert = false"
  ></app-alert-modal>
}

<div class="min-h-screen bg-linear-to-br from-blue-300 via-white-200 to-white-100">
  <!-- Navbar -->
  <nav class="top-0 w-full z-50">
    <div class="max-w-6xl mx-auto px-6 py-6 flex items-center">
      <div class="cursor-pointer" routerLink="">
        <img src="assets/logo.png" alt="AuditFixers Logo" class="h-20 w-auto" />
      </div>
    </div>
  </nav>

  <!-- =========================== -->
  <!-- QUESTIONNAIRE (shown before submission) -->
  <!-- =========================== -->
  @if (!formSubmitted) {
    <div class="flex flex-col justify-start items-center pb-12 px-6">
      <form
        class="w-full max-w-4xl mx-auto bg-white/90 p-8 rounded-2xl shadow-xl backdrop-blur-sm"
        (ngSubmit)="submitForm()"
      >
        <!-- Selected standard badge -->
        @if (selectedStandard) {
          <div class="flex justify-center mb-4">
            <span
              class="inline-block px-4 py-1 rounded-full text-sm font-semibold"
              [class.bg-blue-100]="selectedStandard === '27001'"
              [class.text-blue-700]="selectedStandard === '27001'"
              [class.bg-purple-100]="selectedStandard === '42001'"
              [class.text-purple-700]="selectedStandard === '42001'"
            >
              {{ selectedStandard === '27001' ? 'ISO/IEC 27001:2022 - Information Security' : 'ISO/IEC 42001:2023 - AI Management' }}
            </span>
          </div>
        }

        <!-- Dynamic Heading -->
        <h1 class="text-3xl font-bold mb-2 text-gray-900 text-center">
          {{ selectedStandard === '27001' ? 'ISO 27001 Gap Assessment' : 'ISO 42001 Gap Assessment' }}
        </h1>

        <p class="text-sm text-gray-600 mb-6 text-center">
          Complete this assessment to understand your Organisation’s {{ selectedStandard === '42001' ? 'AI governance posture' : 'security posture' }}.
        </p>

        <app-stepper
          [questions]="questions"
          [currentStep]="currentStep"
          [goToStep]="goToStep.bind(this)"
        ></app-stepper>

        <!-- Questions -->
        <app-questions
          [questions]="questions"
          [currentStep]="currentStep"
        ></app-questions>

        <!-- Summary Step (last step) -->
        @if (questions[currentStep].questions.length === 0) {
          <app-summary-step
            [questions]="questions"
            [currentStep]="currentStep"
            [getStepScore]="getStepScore"
            [getAllScoresTotal]="getAllScoresTotal"
            [radarChartData]="radarChartData"
            [radarChartLabels]="radarChartLabels"
            [radarChartOptions]="radarChartOptions"
            [barChartData]="barChartData"
            [barChartOptions]="barChartOptions"
          ></app-summary-step>
        }

        <!-- Navigation Buttons -->
        <div class="flex justify-between mt-6 text-sm">
          @if (currentStep > 0) {
            <button
              type="button"
              class="flex items-center text-blue-600 font-semibold hover:text-blue-800 focus:outline-none cursor-pointer transition-colors"
              (click)="prevStep()"
            >
              Previous
            </button>
          }

          <div class="flex-1"></div>

          @if (currentStep < questions.length - 1) {
            <button
              type="button"
              class="flex items-center text-blue-600 font-semibold hover:text-blue-800 focus:outline-none cursor-pointer transition-colors disabled:opacity-50"
              (click)="nextStep()"
              [disabled]="!isCurrentStepComplete()"
            >
              Next
            </button>
          }

          @if (currentStep === questions.length - 1) {
            <button
              type="submit"
              [disabled]="isLoading"
              class="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors font-semibold focus:outline-none cursor-pointer flex items-center justify-center gap-2"
            >
              @if (isLoading) {
                <svg
                  class="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              }

              <span>
                {{ isLoading ? 'Processing...' : 'Submit' }}
              </span>
            </button>
          }
        </div>
      </form>
    </div>
  }

  <!-- =========================== -->
  <!-- SUMMARY REPORT (shown only after submission) -->
  <!-- =========================== -->
  <div
    [hidden]="!formSubmitted"
    class="w-full max-w-4xl mx-auto px-6 pb-12"
  >
    <div class="bg-white/90 p-8 rounded-2xl shadow-xl backdrop-blur-sm">
      <!-- Thank you message -->
      <div class="text-center mb-6">
        <div class="text-green-600 text-4xl mb-2">✓</div>
        <h2 class="text-2xl font-bold text-gray-800">Assessment Submitted</h2>
        <p class="text-gray-600">
          Your results are ready below. You can download the full report.
        </p>
      </div>

      <!-- The report component -->
      <app-summary-report
        [companyName]="formData.companyName"
        [contactName]="formData.name"
        [email]="formData.email"
        [assessmentDate]="assessmentDate"
        [sections]="questions"
        [totalGraphicScore]="totalGraphicScore"
        [standard]="selectedStandard || ''"
      ></app-summary-report>

      <!-- Download button -->
      <div class="text-center mt-6">
        <button
          (click)="downloadReport()"
          class="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Download Report
        </button>
      </div>
    </div>
  </div>
</div>